import type { FC } from 'react'
import { Link as RouterLink } from 'react-router'
import { alpha, Box, Card, CardContent, Link, Skeleton, Typography } from '@mui/material'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import { type Product, useMembersQueueSummary } from './MembersDashboardService'
import { GREY, ORANGE, ORANGE_DARK, RED, YELLOW } from '~/shared/lib/theme'
import { MemberAvatar } from './memberDisplay'
import { queuePath } from './queueRoutes'

type Tone = 'overdue' | 'awaiting' | 'onboarding' | 'hp'

interface Props {
  queueKey: string
  title: string
  product: Product
  contract: number | null
  tone: Tone
}

const toneColor = (tone: Tone) => {
  switch (tone) {
    case 'overdue':
      return RED
    case 'awaiting':
      return ORANGE
    case 'onboarding':
      return YELLOW[900]
    default:
      return ORANGE_DARK
  }
}

const PriorityQueueCard: FC<Props> = ({ queueKey, title, product, contract, tone }) => {
  const {
    data,
    isPending: isLoading,
    isError,
  } = useMembersQueueSummary(queueKey, product, contract)
  const count = data?.count ?? 0
  // A failed fetch must not read as an empty queue on a "what needs attention"
  // screen: flag it in the pill (RED) rather than showing a neutral zero.
  const pill = isError ? RED : count > 0 ? toneColor(tone) : GREY[400]

  return (
    <Card
      variant="outlined"
      data-qa={`queue-card-${queueKey}`}
      sx={{ borderColor: 'grey.100', borderRadius: 2, height: '100%', display: 'flex' }}
    >
      <CardContent sx={{ display: 'flex', flexDirection: 'column', flex: 1, width: '100%' }}>
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            {title}
          </Typography>
          <Box
            sx={{
              px: 1.25,
              py: 0.25,
              borderRadius: 5,
              fontSize: 13,
              fontWeight: 600,
              color: pill,
              bgcolor: alpha(pill, 0.14),
              minWidth: 28,
              textAlign: 'center',
            }}
          >
            {isLoading ? (
              <Skeleton width={16} sx={{ display: 'inline-block' }} />
            ) : isError ? (
              '—'
            ) : (
              count
            )}
          </Box>
        </Box>

        <Box sx={{ flex: 1 }}>
          {isLoading ? (
            <Skeleton variant="rectangular" height={72} sx={{ borderRadius: 1 }} />
          ) : isError ? (
            <Typography variant="body2" sx={{ color: RED }}>
              Couldn’t load this queue. Try refreshing.
            </Typography>
          ) : (
            (data?.results ?? []).map(member => (
              <Box
                key={member.id}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 0.75 }}
              >
                <MemberAvatar
                  id={member.id}
                  firstName={member.first_name}
                  lastName={member.last_name}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" noWrap sx={{ color: 'text.primary' }}>
                    {member.first_name} {member.last_name}
                  </Typography>
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{ color: 'text.secondary', display: 'block' }}
                  >
                    {member.reason}
                  </Typography>
                </Box>
              </Box>
            ))
          )}
        </Box>

        <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px solid', borderColor: 'grey.100' }}>
          <Link
            component={RouterLink}
            to={queuePath(queueKey, product, contract)}
            underline="hover"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            View all{isError ? '' : ` ${count}`}
            <ArrowForwardIcon sx={{ fontSize: 16 }} />
          </Link>
        </Box>
      </CardContent>
    </Card>
  )
}

export default PriorityQueueCard
